export * from "./domain/contract.js";
export * from "./domain/determination.js";
export * from "./domain/enforcement.js";
export * from "./domain/errors.js";
export * from "./domain/events.js";
export * from "./domain/evidence.js";
export * from "./domain/identity.js";
export * from "./domain/outcome.js";
export * from "./domain/primitives.js";
export * from "./domain/receipt.js";
export * from "./domain/review.js";
export * from "./domain/run.js";
export * from "./domain/state-machines.js";
export * from "./domain/work-attempt.js";
export * from "./domain/work-item.js";
export * from "./domain/work-snapshot.js";
export * from "./evidence/state/index.js";
export * from "./evidence/graph/index.js";
export * from "./autonomy/index.js";
export * from "./incidents/index.js";
export * from "./outcomes/index.js";
export * from "./ports/index.js";
export * from "./integrations/source-profile-model.js";
export * from "./notifications/model.js";
export * from "./intent/index.js";
export { VERIFICATION_PRODUCT_SCHEMA_VERSION, verificationActorSchema, verificationAuthoritySchema, verificationCheckResultSchema, verificationConsequenceSchema, verificationControlSchema, verificationErrorSchema, verificationEventSchema, verificationEvidenceArtifactSchema, verificationExportPacketSchema, verificationFindingSchema, verificationIdSchema, verificationIsoTimestampSchema, verificationObservationSchema, verificationPolicySchema, verificationProductSchemas, verificationReceiptDecisionSchema, verificationReceiptSchema, verificationRequirementSchema, verificationReviewDecisionSchema, verificationRunSchema, verificationStatusSchema, verificationTenantSchema, } from "./domain/schemas.js";
export { aggregateVerificationStatus } from "./domain/state.js";
/* ── WHETHER A RECEIPT DECIDED ANYTHING ─────────────────────────────────────
 *
 * One derivation, one reader. The derivation is the ONLY way a receipt's
 * `decision` is produced — a mint site cannot hand one in, it can only ask for
 * the one its own checks imply — and the reader is the only sanctioned way to
 * interpret one. Two implementations of "did this decide?" is how a receipt
 * starts disagreeing with itself, which is the `verdict-law.ts` lesson applied
 * one level up.
 */
/** The check kind that decides a CLAIM. Every other kind decides handling. */
export const PREDICATE_DECISION_CHECK_KIND = "predicate-evaluation";
/** How a check names the evaluator that decided it, in `citationRefs`. */
export const RECEIPT_DECISION_EVALUATOR_CITATION_PREFIX = "evaluator:";
/**
 * The statuses that DETERMINE a claim. NOT_RUN, UNKNOWN and PENDING are all
 * "we do not know", however differently they got there, and a receipt that
 * reported any of them as a decision would be the fabricated verdict this
 * product exists to prevent.
 */
const DECIDING_STATUSES = new Set([
    "PASS",
    "FAIL",
    "MISMATCH",
]);
/** Reason code used when a run carried no claim-deciding check at all. */
export const RECEIPT_DECISION_NO_PREDICATE_CHECK = "no-predicate-check";
/**
 * Derive what a receipt must disclose, from the checks the run actually ran.
 *
 * Deliberately takes the CHECKS, not a caller's summary of them: a disclosure
 * assembled beside the checks rather than from them is a field that can drift
 * away from the run it describes, and drift in this particular field is
 * indistinguishable from lying.
 */
export function deriveReceiptDecisionDisclosure(checks) {
    const predicateChecks = checks.filter((check) => check.kind === PREDICATE_DECISION_CHECK_KIND);
    if (predicateChecks.length === 0) {
        return {
            decided: false,
            decidedBy: null,
            predicateStatus: null,
            undecidedReasonCode: RECEIPT_DECISION_NO_PREDICATE_CHECK,
        };
    }
    // Aggregate severity, not "the first one": a run that decided one obligation
    // and left another un-evaluated has not decided the claim.
    const undecided = predicateChecks.find((check) => !DECIDING_STATUSES.has(check.status));
    if (undecided) {
        return {
            decided: false,
            decidedBy: null,
            predicateStatus: undecided.status,
            undecidedReasonCode: undecided.reasonCode ?? "predicate-not-decided",
        };
    }
    const deciding = predicateChecks[0];
    const evaluatorRef = deciding.citationRefs.find((ref) => ref.startsWith(RECEIPT_DECISION_EVALUATOR_CITATION_PREFIX));
    return {
        decided: true,
        // An evaluator the check did not name is one the receipt must not name.
        decidedBy: evaluatorRef ? evaluatorRef.slice(RECEIPT_DECISION_EVALUATOR_CITATION_PREFIX.length) : null,
        predicateStatus: deciding.status,
        undecidedReasonCode: null,
    };
}
/**
 * THE reader. Everything that wants to know whether a receipt decided anything
 * calls this — the offline pack verifier's statement, the CLI, an auditor's own
 * program — so that "what this receipt means" has exactly one answer.
 */
export function readReceiptDecision(receipt) {
    const status = receipt.status;
    const decision = receipt.decision;
    if (decision == null) {
        return {
            disclosed: false,
            status,
            statement: `This receipt does not disclose whether anything was decided. Its status ${status} is the ` +
                `aggregate of checks it names only by id, so it cannot be read as a determination about ` +
                `the claim. Receipts issued before receipts carried this field are signed and immutable; ` +
                `absence here means undisclosed, never undecided.`,
        };
    }
    if (decision.decided) {
        return {
            disclosed: true,
            decided: true,
            status,
            predicateStatus: decision.predicateStatus ?? status,
            decidedBy: decision.decidedBy,
            statement: `A predicate was DECIDED ${decision.predicateStatus ?? status}` +
                `${decision.decidedBy ? ` by ${decision.decidedBy}` : ""}. The receipt's ${status} verdict ` +
                `rests on that determination.`,
        };
    }
    return {
        disclosed: true,
        decided: false,
        status,
        predicateStatus: decision.predicateStatus,
        undecidedReasonCode: decision.undecidedReasonCode ?? "predicate-not-decided",
        statement: `NOTHING WAS DECIDED about the claim (${decision.undecidedReasonCode ?? "predicate-not-decided"}` +
            `${decision.predicateStatus ? `, predicate check ${decision.predicateStatus}` : ", no predicate check"}). ` +
            `The receipt's ${status} verdict reports how the cited evidence was handled, not whether the ` +
            `claim holds.`,
    };
}
export function checkResultCanSupportReceipt(check) {
    if (check.status !== "PASS")
        return false;
    if (check.authority === "semantic")
        return false;
    return check.evidenceArtifactIds.length > 0 && check.citationRefs.length > 0;
}
export function receiptCoversRun(run, receipt) {
    if (receipt.runId !== run.id || receipt.tenantId !== run.tenantId)
        return false;
    if (!run.evidenceHashes || run.evidenceHashes.length === 0 || receipt.evidenceHashes.length === 0) {
        return false;
    }
    if (run.checkResultIds.length === 0)
        return false;
    const receiptCheckIds = new Set(receipt.checkResultIds);
    const receiptEvidenceHashes = new Set(receipt.evidenceHashes);
    return (run.checkResultIds.every((id) => receiptCheckIds.has(id)) &&
        run.evidenceHashes.every((hash) => receiptEvidenceHashes.has(hash)));
}
